import { useTranslation } from "react-i18next";
import { Text } from "@/components/Text";
import { formatList } from "@/i18n/format";
import { busyDuring, busyItems } from "./calendarItems";
import { hourRange, type MeetingDay, minutesOf, onDay } from "./calendarModel";
import { TimeGrid } from "./TimeGrid";
import { useCalendarText } from "./useCalendarText";

/**
 * Where a meeting waiting for approval lands in everyone's day (MHS-969): their busy time that day with the meeting
 * on top, and who's busy then. The start and end are as the card shows them (ISO with the person's offset).
 */
export function MeetingDayPreview({
  day,
  start,
  end,
  organizer,
}: Readonly<{ day: MeetingDay; start: string; end: string | null; organizer: string | null }>) {
  const { t } = useTranslation("chat", { keyPrefix: "calendar" });
  const text = useCalendarText();
  // The card's times carry the person's offset; the day's are wall-clock times in the same zone.
  const from = start.slice(0, 19);
  const to = (end ?? start).slice(0, 19);
  const span = onDay({ start: from, end: to }, day.date);
  if (!span) return null;
  const others = day.people.filter((p) => !p.error);
  const clashes = busyDuring(others, from, to).map((e) => text.who(e, organizer));
  const unreadable = day.people.filter((p) => p.error).length;
  const solo = day.people.length === 1;
  let clashText = t(solo ? "noClashSolo" : "noClash");
  if (clashes.length) clashText = t("clash", { people: formatList(clashes) });
  const sameDay = others
    .flatMap((p) => p.busy)
    .filter((b) => onDay(b, day.date) && b.start.slice(0, 10) === b.end.slice(0, 10));
  const hours = others[0]?.hours ?? { days: [], start: "09:00", end: "17:00" };
  const items = [
    ...busyItems(others, [day.date], (p, b) => text.block(p, b, organizer), { labels: others.length === 1 }),
    {
      id: "meeting",
      date: day.date,
      from: minutesOf(from),
      to: span[1],
      tone: "picked" as const,
      title: t("thisMeeting"),
    },
  ];
  return (
    <div className="space-y-1">
      <Text variant="caption" tone="tertiary">
        {others.length > 1 ? t("everyonesDay") : t("yourDay")}
      </Text>
      <div className="max-w-xs">
        <TimeGrid
          days={[day.date]}
          range={hourRange(hours, [{ start: from, end: to }, ...sameDay])}
          items={items}
          label={others.length > 1 ? t("everyonesDay") : t("yourDay")}
          rowHeight={26}
        />
      </div>
      <Text variant="caption" tone={clashes.length ? "warning" : "success"}>
        {clashText}
      </Text>
      {unreadable > 0 && (
        <Text variant="caption" tone="tertiary">
          {t("unreadableCount", { count: unreadable })}
        </Text>
      )}
    </div>
  );
}
